/**
 * PricingSpotlight — public pricing page for the Priority Roles product.
 * 3-column tier comparison (Free · Pro · Enterprise), highlights the
 * caller's current tier when logged in, and CTA subscribes via the
 * existing /api/subscriptions/subscribe flow (Cashfree).
 *
 * Public route: /pricing/spotlight
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Check, X, Sparkles, ArrowRight } from "lucide-react";
import { trackSpotlightEvent } from "@/lib/spotlightEvents";

const FEATURE_ROWS = [
    { key: "campaigns_limit", label: "Published campaigns", fmt: (v) => v == null ? "Unlimited" : `${v}` },
    { key: "custom_domain",   label: "Custom domain (careers.your.co)" },
    { key: "video_hero",      label: "Video hero background" },
    { key: "sections_advanced", label: "Advanced sections (video, gallery, team, HTML)" },
    { key: "remove_footer",   label: "Remove Portalwiz footer" },
    { key: "custom_css",      label: "Custom CSS (white-label)" },
];

const TIER_ORDER = ["free", "starter", "pro", "enterprise"];

export default function PricingSpotlight() {
    const nav = useNavigate();
    const [searchParams] = useSearchParams();
    const [data, setData] = useState(null);
    const [subscribing, setSubscribing] = useState(null);
    const [authed, setAuthed] = useState(false);

    useEffect(() => {
        // Attribute the view to whatever surface referred them (e.g.
        // ?from=boost&campaign_id=abc from the /campaigns list).
        trackSpotlightEvent(
            "pricing_spotlight_view",
            {
                source: searchParams.get("from") || "direct",
                campaign_id: searchParams.get("campaign_id") || null,
            },
            { dedupe: true },
        );
        (async () => {
            try {
                const { data } = await api.get("/spotlight-campaigns-entitlements");
                setData(data);
                setAuthed(true);
            } catch {
                // Not logged in — fetch just the plans list from /subscriptions/plans.
                try {
                    const { data } = await api.get("/plans", { withCredentials: false });
                    setData({
                        plan_key: null,
                        plans: (data.plans || []).map((p) => ({
                            key: p.key, name: p.name,
                            price_inr: p.price_inr, price_usd: p.price_usd,
                            spotlight: p.spotlight || {},
                            popular: p.popular || false,
                        })),
                    });
                } catch { setData({ plan_key: null, plans: [] }); }
            }
        })();
    }, []);

    const startSubscribe = async (plan_key) => {
        trackSpotlightEvent("pricing_spotlight_cta", {
            plan_key,
            source: searchParams.get("from") || "direct",
            campaign_id: searchParams.get("campaign_id") || null,
        });
        if (!authed) { nav("/login?next=/pricing/spotlight"); return; }
        if (plan_key === "free") { toast.info("You're already on Free — nothing to do here."); return; }
        setSubscribing(plan_key);
        try {
            const { data } = await api.post("/subscription/checkout", { plan_key, currency: "INR", gateway: "cashfree" });
            if (data?.checkout_url) {
                window.location.href = data.checkout_url;
                return;
            }
            if (data?.integration === "cashfree" && data?.payment_session_id) {
                toast.success("Redirecting to Cashfree checkout…");
                // Redirect to a hosted checkout builder route if the app has one, else stay put.
                nav(`/billing?session=${encodeURIComponent(data.payment_session_id)}&plan=${plan_key}`);
                return;
            }
            toast.success("Subscription started");
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSubscribing(null); }
    };

    if (!data) return <div className="min-h-screen grid place-items-center text-xs font-mono uppercase tracking-widest text-muted-foreground">Loading…</div>;

    const plansByKey = Object.fromEntries((data.plans || []).map((p) => [p.key, p]));
    const current = data.plan_key;

    return (
        <div className="min-h-screen bg-background text-foreground" data-testid="pricing-spotlight-page">
            {/* Hero */}
            <section className="border-b border-border">
                <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16 sm:py-24 text-center">
                    <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-mono text-ikb">
                        <Sparkles className="w-3.5 h-3.5" /> Priority Roles pricing
                    </div>
                    <h1 className="mt-4 font-display font-black text-4xl sm:text-6xl leading-[1.05] tracking-tight max-w-3xl mx-auto">
                        Turn urgent hires into branded landing pages
                    </h1>
                    <p className="mt-5 text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
                        Publish a beautiful, shareable micro-site for every hiring campaign — with your fonts, your domain, and analytics that show what&apos;s converting.
                    </p>
                </div>
            </section>

            {/* Tier cards */}
            <section className="max-w-6xl mx-auto px-5 sm:px-8 py-12 sm:py-16">
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6" data-testid="pricing-cards">
                    {TIER_ORDER.map((key) => {
                        const p = plansByKey[key];
                        if (!p) return null;
                        const isCurrent = current === key;
                        const isPopular = p.popular;
                        return (
                            <div key={key}
                                className={`relative bg-white p-6 border-2 flex flex-col ${
                                    isPopular ? "border-ikb" : "border-border"
                                } ${isCurrent ? "ring-4 ring-emerald-500/20" : ""}`}
                                data-testid={`pricing-card-${key}`}>
                                {isPopular && (
                                    <span className="absolute -top-3 left-4 text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 bg-ikb text-white font-bold">
                                        Most popular
                                    </span>
                                )}
                                {isCurrent && (
                                    <span className="absolute -top-3 right-4 text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 bg-emerald-600 text-white font-bold">
                                        Current plan
                                    </span>
                                )}
                                <h2 className="font-display font-black text-2xl">{p.name}</h2>
                                <div className="mt-2 flex items-baseline gap-1">
                                    <span className="font-display font-black text-4xl tracking-tight">₹{p.price_inr.toLocaleString("en-IN")}</span>
                                    <span className="text-xs text-muted-foreground font-mono">/mo</span>
                                </div>
                                <div className="text-[11px] text-muted-foreground font-mono">or ${p.price_usd}/mo</div>

                                <ul className="mt-5 space-y-2 flex-1" data-testid={`pricing-features-${key}`}>
                                    {FEATURE_ROWS.map((row) => {
                                        const val = p.spotlight?.[row.key];
                                        if (row.fmt) {
                                            return (
                                                <li key={row.key} className="flex items-baseline gap-2 text-sm">
                                                    <Check className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                                                    <span>{row.label}: <b>{row.fmt(val)}</b></span>
                                                </li>
                                            );
                                        }
                                        return (
                                            <li key={row.key} className={`flex items-baseline gap-2 text-sm ${val ? "" : "text-muted-foreground"}`}>
                                                {val
                                                    ? <Check className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                                                    : <X className="w-3.5 h-3.5 text-muted-foreground shrink-0" />}
                                                <span>{row.label}</span>
                                            </li>
                                        );
                                    })}
                                </ul>

                                <div className="mt-6 pt-4 border-t border-border">
                                    <button
                                        type="button"
                                        onClick={() => startSubscribe(key)}
                                        disabled={isCurrent || subscribing === key}
                                        className={`w-full h-11 px-4 text-sm font-mono uppercase tracking-widest inline-flex items-center justify-center gap-2 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed ${
                                            isPopular ? "bg-ikb text-white hover:opacity-90" : "border border-border hover:border-ikb hover:text-ikb"
                                        }`}
                                        data-testid={`pricing-cta-${key}`}
                                    >
                                        {isCurrent
                                            ? "Your current plan"
                                            : (subscribing === key
                                                ? "Redirecting…"
                                                : (<>{key === "free" ? "Get started" : `Upgrade to ${p.name}`} <ArrowRight className="w-4 h-4" /></>))}
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
                <p className="mt-6 text-center text-[11px] font-mono text-muted-foreground">
                    Prices in INR. Cashfree checkout · Cancel anytime.
                </p>
            </section>

            {/* Back link for authed users */}
            {authed && (
                <div className="max-w-6xl mx-auto px-5 sm:px-8 pb-16">
                    <Link to="/campaigns" className="text-xs font-mono uppercase tracking-widest text-ikb hover:underline">
                        ← Back to your campaigns
                    </Link>
                </div>
            )}
        </div>
    );
}
